<template>
	<div ref="documentRef" class="document-container">
		<div v-if="loading" class="tableau-loading">
			<p>Chargement du tableau de délibération…</p>
		</div>
		<div v-else-if="error" class="tableau-error">
			<p>{{ error }}</p>
		</div>
		<DeliberationTemplate
			v-else-if="templateProps"
			v-bind="templateProps"
		/>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import type { Deliberation } from '@/types/deliberation'
import DeliberationTemplate from '@/components/templates/DeliberationTemplate.vue'
import { useDeliberation } from '@/composables/useDeliberation'
import { useSessionUser } from '@/composables/useSessionUser'
import { usePersonnelListFull } from '@/composables/usePersonnelListFull'
import { defaultSignatureDirecteur } from '@/components/templates/deliberationTemplateDefaults'
import type { TableauDeliberationExportFilter } from '@/types/documentExport'
import {
	loadAllEtudiantsBis,
	mapTableauDeliberationExport,
	resolveDeliberationContext,
} from '@/utils/documentExportHelpers'

const props = defineProps<{
	deliberation: Deliberation
	exportFilter?: TableauDeliberationExportFilter
}>()

const documentRef = ref<HTMLElement | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const isReady = ref(false)
const templateProps = ref<Record<string, unknown> | null>(null)

const { getTableauDeliberationExport, getDeliberation } = useDeliberation()
const { fetchEtudiantsBisPageSilent } = useSessionUser()
const { fetchPersonnels, personnels } = usePersonnelListFull()

onMounted(async () => {
	try {
		const [exportRes] = await Promise.all([
			getTableauDeliberationExport(props.deliberation.id),
			fetchPersonnels(),
		])

		if (!exportRes.success || !exportRes.data) {
			error.value = exportRes.message || 'Export indisponible'
			return
		}

		let deliberation = props.deliberation
		const ctx = resolveDeliberationContext(deliberation)
		if (!ctx.sessionId || !ctx.specialiteId) {
			const delibRes = await getDeliberation(deliberation.id)
			if (delibRes.success && delibRes.data) deliberation = delibRes.data
		}

		const etudiantsBis = await loadAllEtudiantsBis(fetchEtudiantsBisPageSilent, deliberation)
		const mapped = mapTableauDeliberationExport(
			exportRes.data,
			etudiantsBis,
			deliberation,
			personnels.value,
			props.exportFilter
		)

		templateProps.value = {
			...mapped,
			signatureDirecteur: defaultSignatureDirecteur,
		}
		isReady.value = true
	} catch (e: any) {
		console.error(e)
		error.value = e?.message || 'Erreur lors du chargement'
	} finally {
		loading.value = false
	}
})

defineExpose({ documentRef, isReady })
</script>

<style scoped>
.tableau-loading,
.tableau-error {
	width: 297mm;
	min-height: 80mm;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: Arial, sans-serif;
	font-size: 12pt;
	color: #333;
}
</style>
